import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

const Dashboard: React.FC = () => {
  // Mock data for demonstration
  const stats = [
    {
      title: 'Actionable Emails',
      value: '12',
      icon: <EmailIcon />,
      color: '#1976d2',
    },
    {
      title: 'Pending Deadlines',
      value: '3',
      icon: <ScheduleIcon />,
      color: '#dc004e',
    },
    {
      title: 'Follow-ups Due',
      value: '5',
      icon: <NotificationsIcon />,
      color: '#ed6c02',
    },
    {
      title: 'Processed Today',
      value: '47',
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
    },
  ];

  const recentEmails = [
    {
      subject: 'Project Update Required',
      sender: 'john.doe@company.com',
      priority: 'high',
      time: '2 hours ago',
    },
    {
      subject: 'Meeting Confirmation',
      sender: 'sarah.smith@client.com',
      priority: 'medium',
      time: '4 hours ago',
    },
    {
      subject: 'Budget Review Deadline',
      sender: 'finance@company.com',
      priority: 'high',
      time: '6 hours ago',
    },
  ];

  const upcomingDeadlines = [
    {
      task: 'Submit quarterly report',
      deadline: 'Tomorrow, 5:00 PM',
      priority: 'urgent',
    },
    {
      task: 'Review contract proposal',
      deadline: 'Friday, 2:00 PM',
      priority: 'high',
    },
    {
      task: 'Team meeting preparation',
      deadline: 'Monday, 9:00 AM',
      priority: 'medium',
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Actionable Emails */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Actionable Emails
            </Typography>
            <List>
              {recentEmails.map((email, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={email.subject}
                    secondary={`From: ${email.sender} • ${email.time}`}
                  />
                  <Chip
                    label={email.priority}
                    color={getPriorityColor(email.priority) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Upcoming Deadlines */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Upcoming Deadlines
            </Typography>
            <List>
              {upcomingDeadlines.map((deadline, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={deadline.task}
                    secondary={deadline.deadline}
                  />
                  <Chip
                    label={deadline.priority}
                    color={getPriorityColor(deadline.priority) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;